// ══ TEST SIMULATION (remove after testing) ═══════════════════════════════════
// The single place for every test/simulation parameter in the app. To strip all
// test code: delete this file — the build then fails on each remaining import
// (shared/LayoutV2.jsx, store/multipleEntryPassSlice.js,
// screens/multipleEntryPassPurchase.jsx,
// v2/screens/fitExtendSelector/FitExtendSelector.jsx), so nothing can be
// forgotten silently.
//
// Simulation is inert outside the dev server (import.meta.env.DEV), so a flag
// left on can never affect a production build.

// Simulate a user whose profile has no address, for testing the home →
// location-gate flow (deny the prompt on home with localStorage.clientAddress
// cleared → /location-required). Blanks the five address fields from the Login
// response before they reach the store (applied in shared/LayoutV2.jsx).
// Nothing is sent to the server.
export const SIMULATE_NEW_USER = false;

// Force the OTP flow: silentLogin routes to /phone-validation with a mock
// payload instead of calling Getuserdata, for testing the phone-validation and
// otp-validation screens (applied in shared/LayoutV2.jsx). Nothing reaches the
// server until a submit, which then fails against it — this is for visual QA.
export const SIMULATE_OTP_REQUIRED = false;

// Show the access-type badge (Check-in / Calendar / Phone) on each search
// result row, for QA of the venue flow routing. Rendered only on the local
// dev server (applied in v2/screens/fitExtendSelector/FitExtendSelector.jsx).
export const SHOW_ACCESS_TYPE_BADGES = false;

// Force a web-pass case on the home banner (and every other pass consumer —
// the mock flows through the real slice, selectors and usePassState).
//   null       real API data (simulation off)
//   "none"     never had a pass          → promo row (when eligible)
//   "active"   one active pass, 1/4 used → artwork card + counter
//   "expired"  one expired pass          → promo row when eligible to buy
//                                          again, expired row otherwise
export const SIMULATED_PASS_CASE = null;

// IsPassPurchasePossible in the simulated CanPassPurchasePossible response.
// Set false (with SIMULATED_PASS_CASE = "none" or "expired") to test the
// promo-hidden / expired-row cases.
export const SIMULATED_CAN_PURCHASE = null;

// Force the IsClientHaveCreditCard result on the pass purchase screen
// (applied in screens/multipleEntryPassPurchase.jsx; skips the real API call).
//   null   real API data (simulation off)
//   true   has a card ending 1234 → tappable digits row
//   false  no card                → "add credit card" row, CTA routes to /addCard
export const SIMULATED_HAS_CREDIT_CARD = null;

// TEMP (error-popup QA): fires helpers/swalErrorPopup on the dev preview
// (/dev-preview-home) with this error object after seeding; null = off.
// Optional `partner` overrides the seeded CLALIT to test the legacy Swal branch.
//   { err: { code: 1, mess: "" } }                       server error, no message
//   { err: { code: 1, mess: "אין מנוי פעיל" } }           server error with message
//   { err: { isAxiosError: true, message: "Network Error" } }  network failure
//   { partner: "MEHUDET", err: { code: 1, mess: "" } }   legacy Swal branch
export const SIMULATED_ERROR_POPUP = null;

// ─── plumbing (no test parameters below this line) ───────────────────────────

const isSimulationOn = () => import.meta.env.DEV && SIMULATED_PASS_CASE !== null;

// Mock passes in the real GetAllFreefitWebPass shape. NumberOfEntries counts
// entries already USED, not remaining.
const MOCK_PASSES = {
  none: [],
  active: [
    {
      ID: 999901,
      IsAvailable: true,
      PossibleNumberOfEntries: 4,
      NumberOfEntries: 1,
      StartDate: "01.06.2026 10:00",
      EndDate: "01.06.2031 10:00",
      PurchaseDate: "01.06.2026 10:00",
    },
  ],
  expired: [
    {
      ID: 999902,
      IsAvailable: false,
      PossibleNumberOfEntries: 4,
      NumberOfEntries: 4,
      StartDate: "01.01.2020 10:00",
      EndDate: "01.01.2025 10:00",
      PurchaseDate: "01.01.2020 10:00",
    },
  ],
};

// Returns the mock pass list for the forced case, or null when simulation is off.
export function getSimulatedPasses() {
  if (!isSimulationOn()) return null;
  console.warn(`TEST MODE: simulating web-pass case "${SIMULATED_PASS_CASE}"`);
  return MOCK_PASSES[SIMULATED_PASS_CASE] ?? null;
}

// Returns a mock IsClientHaveCreditCard payload, or null when simulation is off.
export function getSimulatedCreditCard() {
  if (!import.meta.env.DEV || SIMULATED_HAS_CREDIT_CARD === null) return null;
  console.warn(`TEST MODE: simulating HasCreditCard = ${SIMULATED_HAS_CREDIT_CARD}`);
  return SIMULATED_HAS_CREDIT_CARD
    ? { HasCreditCard: true, Last4Digit: "1234" }
    : { HasCreditCard: false, Last4Digit: "" };
}

// Returns a mock CanPassPurchasePossible response, or null when simulation is off.
export function getSimulatedEligibility() {
  if (!isSimulationOn()) return null;
  console.warn(`TEST MODE: simulating IsPassPurchasePossible = ${SIMULATED_CAN_PURCHASE}`);
  return {
    IsPassPurchasePossible: SIMULATED_CAN_PURCHASE,
    IsRegularOrderFound: false,
  };
}
// ══════════════════════════════════════════════════════════════════════════════
